import { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import cn from 'classnames';
// Api
import { GET_STREAM_PARTICIPANTS } from 'api/streams/queries';
// Types
import {
  GetStream_stream_interview_interviewer,
  GetStream_stream_interview_interviewee,
} from 'api/streams/types/GetStream';
import {
  GetStreamParticipants,
  GetStreamParticipantsVariables,
  GetStreamParticipants_getStreamParticipants_entities,
} from 'api/streams/types/GetStreamParticipants';
import {
  SortDirection,
  StreamParticipantsOrderBy,
  UserRole,
} from 'api/graphql-global-types';
// Helpers
import { withCDN } from 'helpers/cdn';
// Components
import ParticipantsListModal from './ParticipantsListModal/ParticipantsListModal';
import SectionDivider from 'components/common3/SectionDivider/SectionDivider';
import FollowButton from 'components/FollowButton/FollowButton';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
import Image from 'ui3/Image/Image';
import LinkButton from 'ui3/LinkButton/LinkButton';
import Text from 'ui3/Text/Text';
// Styles
import styles from './StreamParticipants.module.scss';

type InterviewGuest =
  | GetStream_stream_interview_interviewer
  | GetStream_stream_interview_interviewee;

function mapInterviewGuestToParticipant(
  guest: InterviewGuest
): GetStreamParticipants_getStreamParticipants_entities {
  const storeDetails = guest.storeDetails;
  return {
    __typename: 'User',
    id: guest.id,
    firstName: guest.firstName,
    lastName: guest.lastName,
    slug: guest.slug,
    avatarUrl: storeDetails?.avatarURL ?? null,
    role: UserRole.Athlete,
    storeDetails: storeDetails
      ? {
          __typename: 'StoreDetails',
          id: guest.id,
          storeName: storeDetails.storeName,
          avatarURL: storeDetails.avatarURL,
        }
      : null,
  };
}

type StreamParticipantsProps = {
  streamId: string;
  isInterview?: boolean;
  interviewGuests?: {
    interviewer: GetStream_stream_interview_interviewer | null;
    interviewee: GetStream_stream_interview_interviewee | null;
  } | null;
  withDivider?: boolean;
  withDividerTop?: boolean;
};

const StreamParticipants = ({
  streamId,
  isInterview = false,
  interviewGuests = null,
  withDivider = false,
  withDividerTop = false,
}: StreamParticipantsProps) => {
  const [allParticipants, setAllParticipants] = useState<
    GetStreamParticipants_getStreamParticipants_entities[] | never[]
  >([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const isRefetchingRef = useRef(false);

  const guestsAsParticipants = useMemo(() => {
    if (!isInterview || !interviewGuests) {
      return null;
    }
    const { interviewer, interviewee } = interviewGuests;
    return [interviewer, interviewee]
      .filter((g): g is InterviewGuest => g != null)
      .map(mapInterviewGuestToParticipant);
  }, [isInterview, interviewGuests]);

  const shouldUseInterviewGuests =
    isInterview && guestsAsParticipants && guestsAsParticipants.length > 0;

  const { refetch } = useQuery<
    GetStreamParticipants,
    GetStreamParticipantsVariables
  >(GET_STREAM_PARTICIPANTS, {
    variables: {
      input: {
        streamId,
        orderBy: StreamParticipantsOrderBy.firstName,
        direction: SortDirection.DESC,
        offset: 0,
      },
    },
    onCompleted: (newData: GetStreamParticipants) => {
      const entities = newData?.getStreamParticipants?.entities || [];
      const total = newData?.getStreamParticipants?.total || 0;

      setTotalCount(total);
      // Only update participants if we have entities and they're more than what we currently have
      // This prevents overwriting with fewer participants from cache
      if (entities.length > 0) {
        setAllParticipants((prev) => {
          // If new data has more participants, use it; otherwise keep what we have
          return entities.length >= prev.length ? entities : prev;
        });
      }
    },
    skip: !streamId || Boolean(shouldUseInterviewGuests),
    fetchPolicy: 'cache-and-network',
  });

  // Refetch with total limit if we don't have all participants
  useEffect(() => {
    if (
      shouldUseInterviewGuests ||
      !totalCount ||
      allParticipants.length >= totalCount ||
      !streamId ||
      isRefetchingRef.current
    ) {
      return;
    }
    isRefetchingRef.current = true;
    refetch({
      input: {
        streamId,
        orderBy: StreamParticipantsOrderBy.firstName,
        direction: SortDirection.DESC,
        offset: 0,
        limit: totalCount,
      },
    })
      .then((result) => {
        const entities = result.data?.getStreamParticipants?.entities || [];
        const total = result.data?.getStreamParticipants?.total || 0;
        if (entities.length > 0) {
          setAllParticipants(entities);
          setTotalCount(total);
        }
      })
      .finally(() => {
        isRefetchingRef.current = false;
      });
  }, [
    totalCount,
    allParticipants.length,
    streamId,
    refetch,
    shouldUseInterviewGuests,
  ]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const displayList =
    shouldUseInterviewGuests && guestsAsParticipants
      ? guestsAsParticipants
      : allParticipants;
  const athleteCount = displayList.length;

  // Get first four participants for display
  const firstParticipants = displayList.slice(0, 4);
  const showAllParticipantsButton = displayList.length > 4;

  if (displayList.length === 0) {
    return null;
  }

  const labelText = isInterview ? 'Guests' : 'Participants';
  const buttonText = isInterview ? 'All Guests' : 'All Participants';

  const hasTopDivider = withDividerTop || withDivider;

  return (
    <>
      {hasTopDivider && <SectionDivider />}
      <div
        className={cn(styles.root, {
          [styles.withDivider]: hasTopDivider,
        })}
      >
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <p className={styles.label}>{labelText}</p>
            {!isInterview && (
              <p className={styles.count}>{athleteCount} Athletes</p>
            )}
          </div>
          {showAllParticipantsButton && (
            <button
              type="button"
              onClick={handleOpenModal}
              className={styles.allParticipantsButton}
            >
              {buttonText}
              <MillionsIcon
                name="chevronRight"
                size={16}
                className={styles.arrowIcon}
              />
            </button>
          )}
        </div>
        <div className={styles.participantsRow}>
          {firstParticipants.map(
            ({
              id,
              firstName,
              lastName,
              storeDetails,
              slug,
              avatarUrl,
              role,
            }) => {
              const storeName = storeDetails?.storeName
                ? storeDetails.storeName
                : `${firstName} ${lastName}`;
              const isFanUser = role === UserRole.Customer;
              const participantStore = !isFanUser
                ? ({
                    id,
                    slug,
                    storeDetails,
                  } as any)
                : null;
              return (
                <div key={id} className={styles.logoAndTextWrapper}>
                  <div className={styles.logoWrapper}>
                    <Image
                      className={styles.logoImage}
                      title={storeName}
                      alt={storeName}
                      src={
                        avatarUrl ||
                        storeDetails?.avatarURL ||
                        withCDN('/images/logos/mobile-logo-black.svg')
                      }
                      sizes="10vw"
                    />
                  </div>
                  <div className={styles.infoWrapper}>
                    <Text variant="subtitle2" className={styles.storeName}>
                      {storeName}
                    </Text>
                    <div className={styles.buttonsWrapper}>
                      {participantStore && (
                        <FollowButton
                          store={participantStore}
                          startIcon="plus"
                          unfollowStartIcon="minus"
                          unfollowText="Following"
                          className={styles.followButton}
                          xs="small"
                          followVariant="secondary"
                          unfollowVariant="secondary"
                        />
                      )}
                      {isFanUser ? (
                        <span className={styles.disabledButton}>View</span>
                      ) : (
                        <LinkButton
                          size="small"
                          variant="secondary"
                          subVariant="text"
                          className={styles.viewProfileButton}
                          link={`/${slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View
                        </LinkButton>
                      )}
                    </div>
                  </div>
                </div>
              );
            }
          )}
        </div>
        {withDivider && <SectionDivider noBottomMargin />}

        {isModalOpen && (
          <ParticipantsListModal
            participants={displayList}
            onClose={handleCloseModal}
            isInterview={isInterview}
          />
        )}
      </div>
    </>
  );
};

export default StreamParticipants;
